import { makeStyles, Slide } from '@material-ui/core';
import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';

import { usePlexesMap } from '../../../models/filters/useSelectedPlexIds';
import { useSearchText } from '../../../redux';
import { bottomBorderStyle, spacings, TextSizesEnum } from '../../../theme';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { FilterPicker } from '../common/FilterPicker';
import { usePlexRoute } from '../details/usePlexRoute';
import { EmptySearchState } from './EmptySearchState';
import { SearchLoader } from './SearchLoader';
import { SearchResultItem } from './SearchResultItem';
import { isSearchLoading, useSearch } from './useSearch';

export const SearchResultsPage = React.memo((): React.ReactElement | null => {
  const classes = useStyles();
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchText = useSearchText();
  const [search, setSearchQuery] = useSearch(1, searchContainerRef);
  const plexesMap = usePlexesMap();
  const [plexRoute] = usePlexRoute();

  useEffect(() => {
    setSearchQuery({ input: searchText });
  }, [searchText, setSearchQuery]);

  const isEmpty = search.state === 'done' && search.options.length === 0;

  return (
    <Slide direction={'up'} in={plexRoute.page === 'search'} mountOnEnter={true} unmountOnExit={true}>
      <div className={classes.root} ref={searchContainerRef}>
        <div className={classes.filterPicker}>
          <FilterPicker />
        </div>
        {!isSearchLoading(search) && !isEmpty && (
          <div className={classes.columnsRow}>
            <PlexTypography
              className={classNames(classes.columnTitle, classes.columnType)}
              text={'TYPE'}
              size={TextSizesEnum.XSmall}
            />
            <PlexTypography
              className={classNames(classes.columnTitle, classes.columnDescription)}
              style={{ flex: 1 }}
              text={'DESCRIPTION'}
              size={TextSizesEnum.XSmall}
            />
            <PlexTypography
              className={classNames(classes.columnTitle, classes.columnPlex)}
              text={'PLEX'}
              size={TextSizesEnum.XSmall}
            />
            <PlexTypography
              className={classNames(classes.columnTitle, classes.columnVisibility)}
              text={'VISIBILITY'}
              size={TextSizesEnum.XSmall}
            />
            <PlexTypography
              className={classNames(classes.columnTitle, classes.columnDate)}
              text={'DATE'}
              size={TextSizesEnum.XSmall}
            />
          </div>
        )}
        {isSearchLoading(search) && <SearchLoader />}
        {isEmpty && <EmptySearchState />}
        {(search.state === 'done' || search.state === 'loading_more') &&
          search.options.map((searchResult) => (
            <SearchResultItem
              key={searchResult.id}
              searchResult={searchResult}
              plexName={!!searchResult?.body?.plexId ? plexesMap[searchResult.body.plexId].name : ''}
              plexColor={!!searchResult?.body?.plexId ? plexesMap[searchResult.body.plexId].color : ''}
            />
          ))}
        {search.state === 'loading_more' && <SearchLoader />}
      </div>
    </Slide>
  );
});

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    backgroundColor: '#fff',
    overflow: 'auto',
    position: 'absolute',
    zIndex: 2, // over the star in avatars in plex summary
    left: 0,
    top: 0,
  },
  filterPicker: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 12,
    paddingBottom: 12,
    paddingLeft: spacings.normal,
    ...bottomBorderStyle,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    height: 64,
    alignItems: 'center',
    paddingLeft: spacings.xlarge,
    ...bottomBorderStyle,
  },
  rootTitle: {
    color: '#464663',
    fontWeight: 600,
  },
  columnsRow: {
    display: 'flex',
    flexDirection: 'row',
    flexShrink: 0,
    height: 32,
    alignItems: 'center',
    paddingLeft: 14,
    paddingRight: 14, // change to account for action buttons in the future
    ...bottomBorderStyle,
  },
  columnTitle: {
    display: 'flex',
    color: '#464663',
    fontWeight: 'bold',
  },
  columnType: {
    display: 'flex',
    width: 60,
    marginLeft: 10,
  },
  columnDescription: {
    display: 'flex',
    flex: 1,
  },
  columnPlex: {
    display: 'flex',
    width: 160,
    marginRight: 16,
  },
  columnVisibility: {
    display: 'flex',
    width: 60,
  },
  columnDate: {
    display: 'flex',
    width: 160,
    flexDirection: 'column',
  },
}));
